import axios from 'axios';
import { SERVER_URL } from '../App';
import { processItemData } from '../core/statProcessing';
import { PAGE_SIZE } from '../core/searchConfig';
import { debugLog } from '../utils/debugLog';
import { loadItemCatalog } from './itemCatalog';
import { searchItemsLocally, searchSetsLocally, searchUnifiedLocally } from './localItemSearch';

const LOG_PREFIX = '[Search]';

function summarizeFilters(filters) {
    if (!filters || typeof filters !== 'object') return {};
    const { name, item_type_ids, level_min, level_max, stats, sort_order, lang } = filters;
    return {
        name: name || undefined,
        level_min: level_min ?? undefined,
        level_max: level_max ?? undefined,
        item_types_count: Array.isArray(item_type_ids) ? item_type_ids.length : 0,
        stats_count: stats ? Object.keys(stats).length : 0,
        stat_ids: stats ? Object.keys(stats).slice(0, 5) : [],
        sort_order: sort_order || undefined,
        lang: lang || undefined,
    };
}

function resultIds(results) {
    if (!Array.isArray(results)) return [];
    return results.map((r) => r?.id).filter((id) => id != null);
}

class SearchService {
    /**
     * Items and sets together. Locally this is simply both searches merged and
     * tagged the way the unified endpoint tags them, so the caller cannot tell
     * which side answered.
     */
    async searchUnified(filters, language = 'fr', { sortValue = null } = {}) {
        const endpoint = `${SERVER_URL()}search/`;
        const requestFilters = {
            ...filters,
            limit: filters.limit || PAGE_SIZE,
            lang: language,
        };
        const requestSummary = summarizeFilters(requestFilters);
        const startTime = performance.now?.() || Date.now();

        const catalog = await loadItemCatalog();
        if (catalog) {
            try {
                const merged = searchUnifiedLocally(catalog, requestFilters, language, sortValue);
                // Same enrichment the API branch does: without it the cards have no
                // item type, so their headers come out bare.
                const results = merged.results.map((result) => {
                    if (!result) return result;
                    if (result.search_result_type === 'set') {
                        return Array.isArray(result.items)
                            ? { ...result, items: processItemData(result.items) }
                            : result;
                    }
                    return { ...processItemData(result), search_result_type: 'item' };
                });
                debugLog(`${LOG_PREFIX} Local`, {
                    version: catalog.version, count: results.length, total: merged.total,
                    elapsed_ms: Math.round((performance.now?.() || Date.now()) - startTime),
                });
                return { results, total: merged.total };
            } catch (error) {
                console.warn(`${LOG_PREFIX} local search failed, using the API:`, error);
            }
        }

        debugLog(`${LOG_PREFIX} Request`, { endpoint, filters: requestSummary });
        try {
            const response = await axios.post(endpoint, { filters: requestFilters });
            const { results, total } = response.data;

            // Process mixed results: items and sets
            const processedResults = Array.isArray(results) ? results.map(result => {
                if (!result) return result;

                // Backend uses `type` as a discriminator ('item' | 'set').
                // Preserve it separately as `search_result_type` so we can use
                // `type` for the full item type object populated by processItemData.
                if (result.type === 'item') {
                    const baseItem = {
                        ...result,
                        search_result_type: 'item'
                    };
                    delete baseItem.type;

                    const processedItem = processItemData(baseItem);
                    return {
                        ...processedItem,
                        search_result_type: 'item'
                    };
                }

                if (result.type === 'set') {
                    const baseSet = {
                        ...result,
                        search_result_type: 'set'
                    };
                    delete baseSet.type;

                    if (Array.isArray(baseSet.items)) {
                        return {
                            ...baseSet,
                            items: processItemData(baseSet.items)
                        };
                    }
                    return baseSet;
                }

                return result;
            }) : results;

            const durationMs = (performance.now?.() || Date.now()) - startTime;
            debugLog(`${LOG_PREFIX} Response`, {
                endpoint,
                count: Array.isArray(processedResults) ? processedResults.length : 0,
                ids: resultIds(processedResults),
                total,
                status: response.status,
                elapsed_ms: Math.round(durationMs),
            });
            return { results: processedResults, total };
        } catch (error) {
            const durationMs = (performance.now?.() || Date.now()) - startTime;
            console.error(`${LOG_PREFIX} Error`, {
                endpoint,
                filters: requestSummary,
                elapsed_ms: Math.round(durationMs),
                error: error?.message || String(error)
            });
            throw error;
        }
    }

    /**
     * `sortValue` ranks by something only the caller can compute — the damage
     * sort needs the build's stats. It is answered by the local catalog alone;
     * the API has no such ordering, so a fallback to it silently sorts by level.
     */
    async searchItems(filters, language = 'fr', { sortValue = null } = {}) {
        const endpoint = `${SERVER_URL()}search/items/`;
        const requestFilters = {
            ...filters,
            limit: filters.limit || PAGE_SIZE,
            lang: language,
        };
        const requestSummary = summarizeFilters(requestFilters);
        const startTime = performance.now?.() || Date.now();

        // Local first, on capability rather than on outcome. The catalog either
        // loaded or it did not; "it returned nothing" is a legitimate answer to a
        // search and must not silently re-ask the server, or the queries users
        // notice most would quietly behave differently from the rest.
        const catalog = await loadItemCatalog();
        if (catalog) {
            try {
                const { results, total } = searchItemsLocally(catalog, requestFilters, language, sortValue);
                const processed = processItemData(results);
                debugLog('[ItemSearch] Local', {
                    version: catalog.version,
                    filters: requestSummary,
                    count: processed.length,
                    total,
                    elapsed_ms: Math.round((performance.now?.() || Date.now()) - startTime),
                });
                return { results: processed, total };
            } catch (error) {
                // A bug in local search must not take searching down with it.
                console.warn('[ItemSearch] local search failed, using the API:', error);
            }
        }

        debugLog(`[ItemSearch] Request`, { endpoint, filters: requestSummary });
        try {
            const response = await axios.post(endpoint, { filters: requestFilters });
            const { results, total } = response.data;
            const processedResults = processItemData(results);

            const durationMs = (performance.now?.() || Date.now()) - startTime;
            debugLog(`[ItemSearch] Response`, {
                endpoint,
                count: Array.isArray(processedResults) ? processedResults.length : 0,
                ids: resultIds(processedResults),
                total,
                status: response.status,
                elapsed_ms: Math.round(durationMs),
            });
            return { results: processedResults, total };
        } catch (error) {
            const durationMs = (performance.now?.() || Date.now()) - startTime;
            console.error(`[ItemSearch] Error`, {
                endpoint,
                filters: requestSummary,
                elapsed_ms: Math.round(durationMs),
                error: error?.message || String(error)
            });
            throw error;
        }
    }

    async searchSets(filters, language = 'fr') {
        const endpoint = `${SERVER_URL()}search/sets/`;
        const requestFilters = {
            ...filters,
            limit: filters.limit || PAGE_SIZE,
            lang: language,
        };
        const requestSummary = summarizeFilters(requestFilters);
        const startTime = performance.now?.() || Date.now();

        const catalog = await loadItemCatalog();
        if (catalog) {
            try {
                const { results, total } = searchSetsLocally(catalog, requestFilters, language);
                const processed = results.map((set) => (
                    Array.isArray(set.items) ? { ...set, items: processItemData(set.items) } : set
                ));
                debugLog('[SetSearch] Local', {
                    version: catalog.version, count: processed.length, total,
                    elapsed_ms: Math.round((performance.now?.() || Date.now()) - startTime),
                });
                return { results: processed, total };
            } catch (error) {
                console.warn('[SetSearch] local search failed, using the API:', error);
            }
        }

        debugLog(`[SetSearch] Request`, { endpoint, filters: requestSummary });
        try {
            const response = await axios.post(endpoint, { filters: requestFilters });
            const { results, total } = response.data;

            // Process items for each set to enrich with type information
            const processedResults = Array.isArray(results) ? results.map(set => {
                if (set && set.items) {
                    return {
                        ...set,
                        items: processItemData(set.items)
                    };
                }
                return set;
            }) : results;

            const durationMs = (performance.now?.() || Date.now()) - startTime;
            debugLog(`[SetSearch] Response`, {
                endpoint,
                count: Array.isArray(processedResults) ? processedResults.length : 0,
                ids: resultIds(processedResults),
                total,
                status: response.status,
                elapsed_ms: Math.round(durationMs),
            });
            return { results: processedResults, total };
        } catch (error) {
            const durationMs = (performance.now?.() || Date.now()) - startTime;
            console.error(`[SetSearch] Error`, {
                endpoint,
                filters: requestSummary,
                elapsed_ms: Math.round(durationMs),
                error: error?.message || String(error)
            });
            throw error;
        }
    }

    async getSet(setId, language = 'fr') {
        const endpoint = `${SERVER_URL()}set/${setId}?lang=${language}`;
        const startTime = performance.now?.() || Date.now();
        debugLog(`${LOG_PREFIX} Request`, { endpoint });
        try {
            const response = await axios.get(endpoint);
            const set = response.data;

            if (set && set.items) {
                set.items = processItemData(set.items);
            }

            const durationMs = (performance.now?.() || Date.now()) - startTime;
            debugLog(`${LOG_PREFIX} Response`, {
                endpoint,
                status: response.status,
                elapsed_ms: Math.round(durationMs),
                set_id: set?.id ?? setId,
                items_count: Array.isArray(set?.items) ? set.items.length : 0,
            });
            return set;
        } catch (error) {
            const durationMs = (performance.now?.() || Date.now()) - startTime;
            console.error(`${LOG_PREFIX} Error`, {
                endpoint,
                elapsed_ms: Math.round(durationMs),
                error: error?.message || String(error)
            });
            throw error;
        }
    }
}

const service = new SearchService();
export default service;
